import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { useCurrency } from "../../utils";
import { calcularImpuesto } from "../../utils/calcularImpuesto";
import { crearOperacionFactoring, getProveedoresFactoring } from "../../utils/factoring";
import {
  getMovimientosBancarios,
  getCuentasBancarias,
  vincularMovimientoConFactura,
} from "../../utils/movimientosBancarios";
import {
  getPlanCobros,
  crearPlanCobros,
  eliminarPlanCobros,
  getPlanPagos,
  crearPlanPagos,
  eliminarPlanPagos,
} from "../../utils/planCobros";
import { client } from "../../utils/supabase";

const ACCIONES = [
  { key: "fecha", label: "Ajustar fecha" },
  { key: "cuotas", label: "En cuotas" },
  { key: "vincular", label: "Vincular cobro" },
  { key: "factoring", label: "Factoring" },
];

const PlanCuotasModal = ({ isOpen, onClose, tipo, factura, onSuccess, accionInicial = "fecha" }) => {
  const { id: userId, pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const esCobro = tipo === "cobro";
  const monto = Number(factura?.monto_total || 0);
  const moneda = factura?.moneda || "CLP";
  const fmt = (v) => formatAmount(v, moneda);
  const razonSocial = esCobro ? factura?.razon_social_receptor : factura?.razon_social_emisor;
  const titulo = razonSocial ? `${razonSocial} — Folio ${factura?.folio}` : `Folio ${factura?.folio}`;
  const fechaEmisionFmt = factura?.fecha_emision
    ? (() => {
        const [y, m, d] = factura.fecha_emision.substring(0, 10).split("-");
        return `${d}/${m}/${y}`;
      })()
    : null;

  // Acción activa
  const [accion, setAccion] = useState(accionInicial);

  // Estado compartido
  const [loading, setLoading] = useState(false);

  // Acción 1 — Ajustar fecha
  const [fechaAjuste, setFechaAjuste] = useState("");

  // Acción 2 — Cuotas
  const [cuotas, setCuotas] = useState([]);
  const [numCuotas, setNumCuotas] = useState(2);

  // Acción 3 — Vincular movimiento
  const [movimientos, setMovimientos] = useState([]);
  const [movSeleccionado, setMovSeleccionado] = useState(null);
  const [busquedaMov, setBusquedaMov] = useState("");

  // Acción 4 — Factoring
  const [proveedores, setProveedores] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [formFactoring, setFormFactoring] = useState({
    proveedor_factoring_id: "",
    tasa: "",
    capital_adelantado: "",
    fecha_operacion: new Date().toISOString().split("T")[0],
    fecha_cobro_final: "",
    cuenta_bancaria_id: "",
    notas: "",
  });

  // Cargar datos al abrir
  useEffect(() => {
    if (!isOpen || !factura) return;
    setAccion(accionInicial);
    setMovSeleccionado(null);
    setBusquedaMov("");

    const fechaInicial = esCobro ? factura.plazocobro || "" : factura.plazopago || factura.fecha_display || "";
    setFechaAjuste(fechaInicial);

    setFormFactoring((prev) => ({
      ...prev,
      fecha_cobro_final: factura.plazocobro || "",
      proveedor_factoring_id: "",
      tasa: "",
      capital_adelantado: "",
      notas: "",
    }));

    cargarCuotas();
    if (esCobro) {
      cargarMovimientos();
      cargarFactoring();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, factura]);

  useEffect(() => {
    if (cuentas.length === 0 || !factura?.moneda) return;
    const monedaFactura = factura.moneda;
    const cuentasMoneda = cuentas.filter((c) => c.moneda === monedaFactura);
    if (cuentasMoneda.length === 1) {
      setFormFactoring((p) => ({
        ...p,
        cuenta_bancaria_id: String(cuentasMoneda[0].id),
      }));
    }
  }, [cuentas, factura?.moneda]);

  const cargarCuotas = async () => {
    try {
      const data = esCobro ? await getPlanCobros(factura.id) : await getPlanPagos(factura.id);
      setCuotas(data);
    } catch {
      toast.error("Error cargando cuotas");
    }
  };

  const cargarMovimientos = async () => {
    try {
      const data = await getMovimientosBancarios(pais, {
        estado: "pendiente",
        tipo: "ingreso",
        moneda: factura.moneda || "CLP",
      });
      setMovimientos(data);
    } catch {
      toast.error("Error cargando movimientos");
    }
  };

  const cargarFactoring = async () => {
    try {
      const [provs, ctas] = await Promise.all([getProveedoresFactoring(pais), getCuentasBancarias(pais)]);
      setProveedores(provs);
      setCuentas(ctas);
    } catch {
      toast.error("Error cargando datos de factoring");
    }
  };

  // ── Acción 1: Ajustar fecha ──────────────────────────────────────────────
  const handleGuardarFecha = async () => {
    if (!fechaAjuste) return toast.error("Ingresá una fecha");
    setLoading(true);
    try {
      const campo = esCobro ? "plazocobro" : "plazopago";
      const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
      await client
        .from(tabla)
        .update({ [campo]: fechaAjuste })
        .eq("id", factura.id);
      toast.success("Fecha actualizada");
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  // ── Acción 2: Cuotas ─────────────────────────────────────────────────────
  const generarCuotas = () => {
    if (numCuotas < 2 || numCuotas > 12) return toast.error("Entre 2 y 12 cuotas");
    const montoPorCuota = Math.floor(monto / numCuotas);
    const diferencia = monto - montoPorCuota * numCuotas;
    const hoy = new Date();
    setCuotas(
      Array.from({ length: numCuotas }, (_, i) => {
        const fecha = new Date(hoy);
        fecha.setMonth(fecha.getMonth() + i + 1);
        return {
          numero_cuota: i + 1,
          monto_cuota: i === numCuotas - 1 ? montoPorCuota + diferencia : montoPorCuota,
          fecha_vencimiento: fecha.toISOString().split("T")[0],
          estado: "pendiente",
        };
      })
    );
  };

  const handleCambiarCuota = (idx, field, value) => {
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleGuardarCuotas = async () => {
    if (cuotas.length === 0) return toast.error("Generá al menos una cuota");
    const totalCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota), 0);
    if (Math.abs(totalCuotas - monto) > 1) {
      return toast.error(`Total cuotas ${fmt(totalCuotas)} ≠ monto factura ${fmt(monto)}`);
    }
    setLoading(true);
    try {
      await (esCobro ? eliminarPlanCobros(factura.id) : eliminarPlanPagos(factura.id));
      const payload = cuotas.map((c, i) => ({
        pais,
        ...(esCobro ? { factura_sii_emitida_id: factura.id } : { factura_sii_recibida_id: factura.id }),
        numero_cuota: i + 1,
        monto_cuota: Number(c.monto_cuota),
        fecha_vencimiento: c.fecha_vencimiento,
        estado: c.estado || "pendiente",
        created_by: userId,
      }));
      await (esCobro ? crearPlanCobros(payload) : crearPlanPagos(payload));
      toast.success("Plan guardado");
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  // ── Acción 3: Vincular movimiento ────────────────────────────────────────
  const movFiltrados = movimientos.filter((m) => {
    if (!busquedaMov) return true;
    const q = busquedaMov.toLowerCase();
    return m.descripcion_raw?.toLowerCase().includes(q) || String(m.monto).includes(q);
  });

  const handleVincular = async () => {
    if (!movSeleccionado) return toast.error("Seleccioná un movimiento");
    setLoading(true);
    try {
      await vincularMovimientoConFactura({
        movimiento: movSeleccionado,
        factura,
        esCobro,
        cuotaId: null,
        pais,
        userId,
      });
      toast.success("Movimiento vinculado");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // ── Acción 4: Factoring ──────────────────────────────────────────────────
  const tasaF = parseFloat(formFactoring.tasa) / 100 || 0;
  const capitalAdelantado =
    formFactoring.capital_adelantado !== ""
      ? Number(formFactoring.capital_adelantado)
      : Math.round(monto * (1 - tasaF));
  const costoFinanciero = monto - capitalAdelantado;
  const { impuesto: ivaFinanciamiento } = calcularImpuesto(costoFinanciero, pais);
  const tasaIvaLabel = pais === "Chile" ? 19 : 18;

  const handleProveedorChange = (id) => {
    const prov = proveedores.find((p) => p.id === parseInt(id, 10));
    setFormFactoring((prev) => ({
      ...prev,
      proveedor_factoring_id: id,
      tasa: prov ? (prov.tasa_habitual * 100).toFixed(2) : "",
      capital_adelantado: prov ? String(Math.round(monto * (1 - prov.tasa_habitual))) : "",
    }));
  };

  const handleGuardarFactoring = async () => {
    if (!formFactoring.proveedor_factoring_id) return toast.error("Seleccioná un proveedor");
    if (!formFactoring.cuenta_bancaria_id) return toast.error("Seleccioná una cuenta bancaria");
    if (!capitalAdelantado) return toast.error("Ingresá el capital adelantado");
    setLoading(true);
    try {
      await crearOperacionFactoring({
        pais,
        factura_sii_emitida_id: factura.id,
        proveedor_factoring_id: parseInt(formFactoring.proveedor_factoring_id, 10),
        monto_factura: monto,
        capital_adelantado: capitalAdelantado,
        tasa: tasaF,
        costo_financiero: costoFinanciero,
        iva_financiamiento: ivaFinanciamiento,
        fecha_operacion: formFactoring.fecha_operacion,
        fecha_cobro_final: formFactoring.fecha_cobro_final || null,
        cuenta_bancaria_id: parseInt(formFactoring.cuenta_bancaria_id, 10),
        notas: formFactoring.notas || null,
        created_by: userId,
      });
      toast.success("Operación de factoring registrada");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al registrar factoring: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  if (!factura) return null;

  const totalCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const diferenciaCuotas = monto - totalCuotas;

  // Tabs visibles según tipo
  const accionesVisibles = esCobro ? ACCIONES : ACCIONES.filter((a) => a.key !== "vincular" && a.key !== "factoring");

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          {titulo}
          {fechaEmisionFmt && (
            <span style={{ fontSize: "0.75rem", fontWeight: 400, color: "#888", marginLeft: 8 }}>
              · Fac. {fechaEmisionFmt}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Tabs de acción */}
        <div style={{ display: "flex", width: "100%", marginBottom: 20, gap: 8 }}>
          {accionesVisibles.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => setAccion(a.key)}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: "1px solid #dee2e6",
                borderRadius: 6,
                cursor: "pointer",
                background: accion === a.key ? "#2c3e50" : "transparent",
                color: accion === a.key ? "#fff" : "#666",
                textAlign: "center",
              }}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* ── Acción 1: Ajustar fecha ── */}
        {accion === "fecha" && (
          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
              {esCobro ? "Fecha de cobro estimada" : "Fecha de pago estimada"}
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaAjuste}
              onChange={(e) => setFechaAjuste(e.target.value)}
              style={{ maxWidth: 200 }}
            />
          </div>
        )}

        {/* ── Acción 2: Cuotas ── */}
        {accion === "cuotas" && (
          <>
            {cuotas.length === 0 && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: "0.85rem" }}>Generar</span>
                <input
                  type="number"
                  min={2}
                  max={12}
                  value={numCuotas}
                  onChange={(e) => setNumCuotas(parseInt(e.target.value))}
                  className="form-control form-control-sm"
                  style={{ width: 60 }}
                />
                <span style={{ fontSize: "0.85rem" }}>cuotas iguales</span>
                <button className="btn btn-sm btn-primary" onClick={generarCuotas}>
                  Generar
                </button>
              </div>
            )}
            {cuotas.length > 0 && (
              <>
                <table className="table table-sm table-bordered" style={{ fontSize: "0.83rem" }}>
                  <thead className="table-dark">
                    <tr>
                      <th>N°</th>
                      <th>Fecha vencimiento</th>
                      <th style={{ textAlign: "right" }}>Monto</th>
                      <th style={{ textAlign: "center" }}>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {cuotas.map((c, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={c.fecha_vencimiento || ""}
                            onChange={(e) => handleCambiarCuota(i, "fecha_vencimiento", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={c.monto_cuota || ""}
                            onChange={(e) => handleCambiarCuota(i, "monto_cuota", e.target.value)}
                            style={{ textAlign: "right" }}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 6px",
                              borderRadius: 3,
                              background: c.estado === "cobrado" || c.estado === "pagado" ? "#d4edda" : "#fff3cd",
                              color: c.estado === "cobrado" || c.estado === "pagado" ? "#155724" : "#856404",
                            }}
                          >
                            {c.estado || "Pendiente"}
                          </span>
                        </td>
                        <td>
                          <button
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b" }}
                            onClick={() => setCuotas((prev) => prev.filter((_, idx) => idx !== i))}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ textAlign: "right", fontSize: "0.83rem", marginTop: 8 }}>
                  <span>
                    Total: <strong>{fmt(totalCuotas)}</strong>
                  </span>
                  {Math.abs(diferenciaCuotas) > 0 && (
                    <span style={{ marginLeft: 12, color: "#c0392b" }}>Diferencia: {fmt(diferenciaCuotas)}</span>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  style={{ marginTop: 8 }}
                  onClick={() => setCuotas([])}
                >
                  Reiniciar
                </button>
              </>
            )}
          </>
        )}

        {/* ── Acción 3: Vincular movimiento ── */}
        {accion === "vincular" && (
          <div>
            <input
              className="form-control form-control-sm"
              placeholder="Buscar por descripción o monto..."
              value={busquedaMov}
              onChange={(e) => setBusquedaMov(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            {movFiltrados.length === 0 ? (
              <div style={{ color: "#888", fontSize: "0.85rem", padding: "12px 0" }}>
                Sin movimientos de cobro pendientes
              </div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.82rem" }}>
                  <thead className="table-dark" style={{ position: "sticky", top: 0 }}>
                    <tr>
                      <th style={{ width: "15%" }}>Fecha</th>
                      <th style={{ width: "60%", textAlign: "left" }}>Descripción</th>
                      <th style={{ width: "25%", textAlign: "right" }}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movFiltrados.map((m) => (
                      <tr
                        key={m.id}
                        onClick={() => setMovSeleccionado(movSeleccionado?.id === m.id ? null : m)}
                        style={{
                          cursor: "pointer",
                          background: movSeleccionado?.id === m.id ? "#d4edda" : "transparent",
                        }}
                      >
                        <td style={{ width: "15%", whiteSpace: "nowrap" }}>{m.fecha?.slice(5)}</td>
                        <td style={{ textAlign: "left" }} title={m.descripcion_raw}>
                          {m.descripcion_raw?.slice(0, 45)}
                          {m.descripcion_raw?.length > 45 ? "…" : ""}
                        </td>
                        <td style={{ textAlign: "right", color: "#27ae60", fontWeight: 600 }}>
                          {fmt(Math.abs(m.monto))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {movSeleccionado && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  background: "#d4edda",
                  borderRadius: 4,
                  fontSize: "0.82rem",
                }}
              >
                Seleccionado: <strong>{movSeleccionado.descripcion_raw?.slice(0, 50)}</strong> —{" "}
                {fmt(Math.abs(movSeleccionado.monto))} — {movSeleccionado.fecha}
              </div>
            )}
          </div>
        )}

        {/* ── Acción 4: Factoring ── */}
        {accion === "factoring" && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Proveedor de factoring
              </label>
              <select
                className="form-control form-control-sm"
                value={formFactoring.proveedor_factoring_id}
                onChange={(e) => handleProveedorChange(e.target.value)}
              >
                <option value="">Seleccionar...</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                    {p.tipo === "pronto_pago" ? " (pronto pago)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Tasa (%)
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={formFactoring.tasa}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, tasa: e.target.value, capital_adelantado: "" }))}
                  placeholder="ej: 3.5"
                  step="0.01"
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Capital adelantado ($)
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={formFactoring.capital_adelantado || capitalAdelantado}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, capital_adelantado: e.target.value }))}
                />
              </div>
            </div>
            <div
              style={{
                background: "#fff3cd",
                borderRadius: 6,
                padding: "10px 14px",
                marginBottom: 12,
                fontSize: "0.82rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Costo financiero:</span>
                <span style={{ color: "#c0392b" }}>{fmt(costoFinanciero)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span>
                  {pais === "Chile" ? "IVA" : "IGV"} financiamiento ({tasaIvaLabel}%):
                </span>
                <span style={{ color: "#c0392b" }}>{fmt(ivaFinanciamiento)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontWeight: 600 }}>
                <span>Total costo:</span>
                <span style={{ color: "#c0392b" }}>{fmt(costoFinanciero + ivaFinanciamiento)}</span>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Fecha operación
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formFactoring.fecha_operacion}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, fecha_operacion: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Fecha cobro final
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formFactoring.fecha_cobro_final}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, fecha_cobro_final: e.target.value }))}
                />
              </div>
            </div>
            {(() => {
              const monedaFactura = factura?.moneda || "CLP";
              const cuentasMoneda = cuentas.filter((c) => c.moneda === monedaFactura);

              // Si hay una sola cuenta en esa moneda, no mostrar el selector
              if (cuentasMoneda.length === 1) {
                return (
                  <div style={{ marginBottom: 12, fontSize: "0.82rem", color: "#555" }}>
                    <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Cuenta bancaria destino
                    </label>
                    <span>{`${cuentasMoneda[0].bancos?.nombre || "Banco"} ${cuentasMoneda[0].moneda} — ${cuentasMoneda[0].numero_cuenta}`}</span>
                  </div>
                );
              }

              // Si hay múltiples cuentas en esa moneda, mostrar selector
              return (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Cuenta bancaria destino
                  </label>
                  <select
                    className="form-control form-control-sm"
                    value={formFactoring.cuenta_bancaria_id}
                    onChange={(e) => setFormFactoring((p) => ({ ...p, cuenta_bancaria_id: e.target.value }))}
                  >
                    <option value="">Seleccionar...</option>
                    {cuentasMoneda.map((c) => (
                      <option key={c.id} value={c.id}>
                        {`${c.bancos?.nombre || "Banco"} ${c.moneda} — ${c.numero_cuenta}`}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Notas (opcional)
              </label>
              <textarea
                className="form-control form-control-sm"
                rows={2}
                value={formFactoring.notas}
                onChange={(e) => setFormFactoring((p) => ({ ...p, notas: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={loading}
          onClick={() => {
            if (accion === "fecha") handleGuardarFecha();
            else if (accion === "cuotas") handleGuardarCuotas();
            else if (accion === "vincular") handleVincular();
            else if (accion === "factoring") handleGuardarFactoring();
          }}
        >
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanCuotasModal;
